import React, { useEffect } from 'react'
import { useSellMoneyStore } from '@/store/useSellMoneyStore'
import { useCalculateConversionFee } from '@/domain/transfer/useCases/useCalculateConversionFee'
import { currencyUtils } from '@/utils'
import { useDebounceCallBack } from '@/hooks/useDebounce'

const { parseAmountToFloat } = currencyUtils

const MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE = 1.01

export const SellMoneyEstimatedFee: React.FC = () => {
  const cryptoAmount = useSellMoneyStore((state) => state.cryptoAmount)
  const fiatAmount = useSellMoneyStore((state) => state.fiatAmount)
  const fromCurrency = useSellMoneyStore((state) => state.fromCurrency)
  const toCurrency = useSellMoneyStore((state) => state.toCurrency)
  const setCalculatedFeeAmount = useSellMoneyStore(
    (state) => state.setCalculatedFeeAmount,
  )
  const setCalculatedCreditAmount = useSellMoneyStore(
    (state) => state.setCalculatedCreditAmount,
  )
  const setCalculatedTotalDebitAmount = useSellMoneyStore(
    (state) => state.setCalculatedTotalDebitAmount,
  )
  const setHasErrorOnFeeCalculation = useSellMoneyStore(
    (state) => state.setHasErrorOnFeeCalculation,
  )

  const thereIsLessThanMin =
    parseAmountToFloat({
      amount: fiatAmount.amount,
      decimalSeparator: '.',
    }) < MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE

  const { calculateConversionFee } = useCalculateConversionFee({
    onSuccess: ({ fee_amount, credit_amount, total_debit_amount }) => {
      setHasErrorOnFeeCalculation(false)
      setCalculatedFeeAmount(fee_amount.amount)
      setCalculatedCreditAmount(credit_amount?.amount || '0')
      setCalculatedTotalDebitAmount(total_debit_amount.amount)
    },
    onError: () => {
      setHasErrorOnFeeCalculation(true)
      setCalculatedFeeAmount(undefined)
      setCalculatedCreditAmount(undefined)
      setCalculatedTotalDebitAmount(undefined)
    },
  })

  const triggerFeeCalculation = async () => {
    if (!fromCurrency || !toCurrency) {
      return
    }

    const feePayload = {
      credit_currency: toCurrency.code as any,
      debit_amount: cryptoAmount.amount,
      debit_currency: fromCurrency.code as any,
    }

    await calculateConversionFee(feePayload)
  }

  const debounceOnChangeInputs = useDebounceCallBack(() => {
    // IF THE VALUE IS LESS THAN MINIMUM, RESET FEE
    if (thereIsLessThanMin) {
      setCalculatedFeeAmount('0')
      setCalculatedCreditAmount('0')
      setCalculatedTotalDebitAmount('0')
      return
    }

    triggerFeeCalculation()
  }, 500)

  useEffect(() => {
    if (!fromCurrency || !toCurrency) {
      return
    }

    const cryptoAmountFloat = parseAmountToFloat({
      amount: cryptoAmount.amount,
      decimalSeparator: '.',
    })

    // Only calculate if there's a valid amount
    if (cryptoAmountFloat > 0) {
      debounceOnChangeInputs()
    } else {
      // Reset fee when amount is 0
      setCalculatedFeeAmount(undefined)
      setCalculatedCreditAmount(undefined)
      setCalculatedTotalDebitAmount(undefined)
    }
  }, [cryptoAmount.amount, fromCurrency, toCurrency])

  // This component doesn't render anything, it just handles fee calculation
  return null
}
